import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {string | undefined} */
  #gangOption;

  /** @type {string | undefined} */
  #gangIndex;

  /** @type {string | undefined} */
  #currentGangValue;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      Theme.template.name === 'product' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(event.target), loadsNewProduct);

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (event.target.dataset.variantId) {
        url.searchParams.set('variant', event.target.dataset.variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Validates and sets up gang option functionality.
   */
  validateGang() {
    const sectionId = this.dataset.section || this.dataset.productId;
    const mediaItemWithGang = document.querySelector(`[data-gang-option]`);
    if (mediaItemWithGang) {
      this.#gangOption = mediaItemWithGang.dataset.gangOption;
    }
  }

  /**
   * Updates gang media based on selected variant.
   * @param {Object} currentVariant - The current variant data.
   */
  updateGangMedia(currentVariant, variantDataCore = null) {
    if (!this.#gangOption) return;
    const variantInputWrapper = this.querySelector(`.variant-input-wrapper[data-option-slug="${this.#gangOption}"]`);
    if (variantInputWrapper) {
      this.#gangIndex = variantInputWrapper.dataset.optionIndex;
    } else {
      this.#gangOption = null;
      return;
    }
    if (!currentVariant) return;
    const sectionId = this.dataset.section || this.dataset.productId;
    const mediaGallery = document.getElementById(`horizontal-media-gallery-${sectionId}`);
    const currentOptionKey = this.#gangIndex;
    // const gangValue = this.getSlug(currentVariant[currentOptionKey]);
    // console.log(currentOptionKey);
    //  console.log(currentVariant);

    // Access color from metafields if available
    const colorCode = variantDataCore?.metafields?.color_code || null;

    // Use colorCode if available, otherwise use the original gangValue logic
    const gangValue = colorCode && colorCode.trim() !== '' 
      ? this.getSlug(colorCode) 
      : this.getSlug(currentVariant[currentOptionKey]);
    console.log('variant value:', currentVariant[this.#gangIndex]);
    console.log('slug:', gangValue);
    if (this.#currentGangValue !== gangValue) {
      // Remove active class from all gang connected elements
      document.querySelectorAll(
        `[data-gang-connect]`
      ).forEach(function(media){
        media.classList.remove('gang__active');
      });

      this.#currentGangValue = gangValue;
      const gangConnect = this.#gangOption + '_' + gangValue;
      const newMedias = document.querySelectorAll(
        `[data-gang-connect="${gangConnect}"]`
      );


          console.log(gangConnect);

      
      if (!newMedias.length) return;
      
      // Add active class and reorder media elements
      Array.from(newMedias).reverse().forEach(function(newMedia) {
        newMedia.classList.add('gang__active');
        let parent = newMedia.parentElement;
        if (parent.firstChild != newMedia) {
          parent.prepend(newMedia);
          parent.classList.add('product__media-no-animate');
        }
      });

      // Update thumbnail slider
      const thumbnailSlider = document.getElementById(`GalleryThumbnails-${sectionId}`);
      if (thumbnailSlider) {
        const inactiveItems = thumbnailSlider.querySelectorAll('.thumbnail-list__item:not(.gang__active)');
        thumbnailSlider.slider.dataset.mediaCount = thumbnailSlider.sliderItems.length - inactiveItems.length;
        if (thumbnailSlider.slider.dataset.mediaCount < 2) {
          thumbnailSlider.classList.add('hidden');
        } else {
          thumbnailSlider.classList.remove('hidden');
        }
        thumbnailSlider.resetPages();
      }

      // Update product gallery
      if (!mediaGallery) {
        const productGallery = document.querySelector(`#shopify-section-${sectionId} product-gallery`)
        if (productGallery) {
          productGallery.resetPages();
          productGallery.goToFirstSlide();
        }
      }

      // Update gallery slider buttons
      const gallerySliderButtons = document.querySelector(`#shopify-section-${sectionId} product-gallery .slider-buttons`);
      if (gallerySliderButtons) {
        if (document.querySelectorAll(`#shopify-section-${sectionId} product-gallery .product__media-item.gang__active`).length < 2) {
          gallerySliderButtons.classList.add('hidden');
        } else {
          gallerySliderButtons.classList.remove('hidden');
        }
      }
    }

    // Handle case when variant has no featured media
    if (!currentVariant.featured_media) {
      if(!document.querySelectorAll(`#shopify-section-${sectionId} product-gallery .product__media-item.gang__active.is-active, quick-view-drawer details[open] product-gallery .product__media-item.gang__active.is-active`).length) {
        const firstGangMedia = document.querySelector(`#shopify-section-${sectionId} product-gallery .product__media-item.gang__active, quick-view-drawer details[open] product-gallery .product__media-item.gang__active`);
        if (firstGangMedia) {
          firstGangMedia.classList.add('is-active');
        }
      }
      return;
    }
    
    // Update media gallery with featured media
    if (mediaGallery) {
      const scrollIntoView = typeof this.dataset.noScroll === 'undefined';
      //mediaGallery.setActiveMedia(`${sectionId}-${currentVariant.featured_media.id}`, true, scrollIntoView);
    }
    
    // Update modal content
    const modalContent = document.querySelector(`#ProductModal-${sectionId} .product-media-modal__content`);
    if (modalContent) {
      const newMediaModal = modalContent.querySelector(`[data-media-id="${currentVariant.featured_media.id}"]`);
      if (newMediaModal) {
        modalContent.prepend(newMediaModal);
      }
    }
    
    // Update thumbnail content
    const thumbnailContent = document.querySelector(`#ProductThumbnails-${sectionId}`);
    if (thumbnailContent) {
      const newMediaThumbnail = thumbnailContent.querySelector(`[data-media-id="${currentVariant.featured_media.id}"]`);
      if (newMediaThumbnail) {
        thumbnailContent.prepend(newMediaThumbnail);
      }
    }
  }

  /**
   * Converts a string to a slug format.
   * @param {string} someString - The string to convert.
   * @returns {string} The slugified string.
   */
  getSlug(someString) {
    return someString.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-$/, '').replace(/^-/, '');
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        // Validate gang option before updating
        this.validateGang();

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            const variantDataCore = JSON.parse(textContent);
            const variantData = JSON.parse(textContent).variant;
            const currentVariant = this.getCurrentVariant(variantData);
            
            // Update gang media if gang option is available - now passing variantDataCore
            if (this.#gangOption && variantData) {
              this.updateGangMedia(variantData, variantDataCore);
            }

            this.dispatchEvent(
              new VariantUpdateEvent(variantData, this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * Gets the current variant from variant data based on selected options.
   * @param {Array|Object} variantData - The variant data array or object.
   * @returns {Object|undefined} The current variant object.
   */
  getCurrentVariant(variantData) {
    const selectedValues = this.selectedOptionsValues;
    console.log(selectedValues);
    if (!selectedValues.length) return undefined;

    // Handle different data structures
    let variants = Array.isArray(variantData) ? variantData : 
                  (variantData?.variants ? variantData?.variants : 
                  (variantData?.product && variantData.product.variants ? variantData.product.variants : []));

    if (!Array.isArray(variants)) {
      console.warn('Variant data is not in expected format:', variantData);
      return undefined;
    }

    return variants.find(variant => {
      // Check if variant has option_values property
      if (variant.option_values) {
        return variant.option_values.every(optionValue => 
          selectedValues.includes(optionValue.id.toString())
        );
      }
      
      // Fallback: check by options array (e.g., ["Red", "Large", "Cotton"])
      if (variant.options && Array.isArray(variant.options)) {
        // Get selected option elements to match with variant options
        const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));
        
        return variant.options.every((optionValue, index) => {
          const selectedOption = selectedOptions[index];
          return selectedOption && (selectedOption.value === optionValue || selectedOption.textContent.trim() === optionValue);
        });
      }

      return false;
    });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}